import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCornerLeftUp,
  IconFile,
  IconFolder,
  IconRefresh,
} from '@tabler/icons-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { api } from '../api.js';
import { formatBytes, relativeTime } from '@/lib/utils';

// Browse the R2 backup bucket ("cold storage"). Everything here reads a
// postgres copy of the bucket listing; R2 is only listed when an admin
// presses Refresh, because every list call is billed (Class A, ~1 per
// 1000 objects). See backend/src/backups/manifest.ts.

const CLASS_A_USD_PER_MILLION = 4.5;

export function AdminColdStorageSection() {
  const qc = useQueryClient();
  const [path, setPath] = useState('');
  const [filter, setFilter] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const status = useQuery({
    queryKey: ['admin.cold.status'],
    queryFn: api.adminColdStatus,
    // Polls postgres only (free), and only while a refresh runs.
    refetchInterval: (q) => (q.state.data?.running ? 5_000 : false),
  });
  const running = status.data?.running ?? false;
  const currentId = status.data?.current?.id;

  const listing = useQuery({
    queryKey: ['admin.cold.list', currentId, path],
    queryFn: () => api.adminColdList(path),
    enabled: currentId !== undefined,
  });

  // A refresh finished → the new listing may not contain the folder we
  // were in; jump back to the root.
  const prevId = useRef(currentId);
  useEffect(() => {
    if (prevId.current !== undefined && currentId !== prevId.current) setPath('');
    prevId.current = currentId;
  }, [currentId]);

  const refresh = useMutation({
    mutationFn: api.adminColdRefresh,
    onSettled: () => {
      setConfirmOpen(false);
      void qc.invalidateQueries({ queryKey: ['admin.cold.status'] });
    },
  });

  if (status.isLoading) return <Spinner size="xs" />;
  if (status.error) {
    if (/admin only/i.test(status.error.message)) return null;
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertDescription>{status.error.message}</AlertDescription>
      </Alert>
    );
  }

  const current = status.data?.current ?? null;
  const latest = status.data?.latest ?? null;
  const estCalls = current?.listCalls ?? (current?.objects ? Math.ceil(current.objects / 1000) : 500);
  const estUsd = (estCalls * CLASS_A_USD_PER_MILLION) / 1_000_000;

  const goTo = (p: string) => {
    setPath(p);
    setFilter('');
  };
  const segs = path ? path.split('/') : [];
  const needle = filter.trim().toLowerCase();
  const dirs = (listing.data?.dirs ?? []).filter((d) => d.name.toLowerCase().includes(needle));
  const files = (listing.data?.files ?? []).filter((f) => f.name.toLowerCase().includes(needle));

  return (
    <div className="flex flex-col gap-3">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 py-3">
          <div className="min-w-0 flex-1 text-sm">
            {current ? (
              <>
                <span className="font-medium">
                  {formatBytes(current.bytes ?? 0)} · {(current.objects ?? 0).toLocaleString()} objects
                </span>
                <span className="text-muted-foreground">
                  {' '}— listed {relativeTime(current.finishedAt)}
                  {current.triggeredBy ? ` by ${current.triggeredBy}` : ''}. Not live: changes since
                  then show up after the next refresh.
                </span>
              </>
            ) : (
              <span className="text-muted-foreground">
                Cold storage hasn&apos;t been listed yet. Press Refresh to take the first listing.
              </span>
            )}
          </div>
          {running ? (
            <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <Spinner size="xs" /> Listing bucket… (~5 min)
            </span>
          ) : (
            <Button variant="secondary" onClick={() => setConfirmOpen(true)}>
              <IconRefresh />
              Refresh cold storage listings
            </Button>
          )}
        </CardContent>
      </Card>

      {latest?.status === 'failed' && !running && (
        <Alert tone="warning">
          <IconAlertTriangle />
          <AlertDescription>
            Last refresh failed {relativeTime(latest.finishedAt)}: {latest.error ?? 'unknown error'}
            {current ? ' — showing the previous listing.' : ''}
          </AlertDescription>
        </Alert>
      )}

      {current && (
        <div className="overflow-hidden rounded-md border border-border">
          <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
            <nav className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-sm text-muted-foreground">
              {segs.length === 0 ? (
                <span className="font-semibold text-foreground">bucket</span>
              ) : (
                <button type="button" onClick={() => goTo('')} className="text-theia-400 hover:underline">
                  bucket
                </button>
              )}
              {segs.map((seg, i) => {
                const sub = segs.slice(0, i + 1).join('/');
                return (
                  <span key={sub} className="flex items-center gap-1">
                    <span>/</span>
                    {i === segs.length - 1 ? (
                      <span className="text-foreground">{seg}</span>
                    ) : (
                      <button type="button" onClick={() => goTo(sub)} className="text-theia-400 hover:underline">
                        {seg}
                      </button>
                    )}
                  </span>
                );
              })}
            </nav>
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter this folder…"
              className="h-7 w-48 text-xs"
            />
          </div>
          {listing.isLoading ? (
            <div className="p-4">
              <Spinner size="xs" />
            </div>
          ) : listing.error ? (
            <p className="px-3 py-4 text-sm text-red-400">{listing.error.message}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="px-3 py-1.5 font-medium">Name</th>
                  <th className="w-28 px-3 py-1.5 text-right font-medium">Size</th>
                  <th className="hidden w-28 px-3 py-1.5 text-right font-medium sm:table-cell">Objects</th>
                  <th className="hidden w-36 px-3 py-1.5 text-right font-medium sm:table-cell">Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {path && (
                  <tr className="border-b border-border/50 hover:bg-muted/40">
                    <td colSpan={4} className="px-3 py-1.5">
                      <button
                        type="button"
                        onClick={() => goTo(segs.slice(0, -1).join('/'))}
                        className="flex items-center gap-2 text-muted-foreground"
                      >
                        <IconCornerLeftUp size={16} /> ..
                      </button>
                    </td>
                  </tr>
                )}
                {dirs.map((d) => (
                  <tr key={`d:${d.name}`} className="border-b border-border/50 hover:bg-muted/40">
                    <td className="px-3 py-1.5">
                      <button
                        type="button"
                        onClick={() => goTo(path ? `${path}/${d.name}` : d.name)}
                        className="flex items-center gap-2 text-left hover:text-theia-400 hover:underline"
                      >
                        <IconFolder size={16} className="shrink-0 fill-theia-400/20 text-theia-400" />
                        <span className="break-all">{d.name}</span>
                      </button>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatBytes(d.bytes)}</td>
                    <td className="hidden px-3 py-1.5 text-right tabular-nums text-muted-foreground sm:table-cell">
                      {d.objects.toLocaleString()}
                    </td>
                    <td className="hidden px-3 py-1.5 text-right text-muted-foreground sm:table-cell">
                      {d.newest ? relativeTime(d.newest) : '—'}
                    </td>
                  </tr>
                ))}
                {files.map((f) => (
                  <tr key={`f:${f.name}`} className="border-b border-border/50 last:border-0 hover:bg-muted/40">
                    <td className="px-3 py-1.5">
                      <span className="flex items-center gap-2">
                        <IconFile size={16} className="shrink-0 text-muted-foreground" />
                        <span className="break-all font-mono text-xs">{f.name}</span>
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatBytes(f.size)}</td>
                    <td className="hidden px-3 py-1.5 sm:table-cell" />
                    <td
                      className="hidden px-3 py-1.5 text-right text-muted-foreground sm:table-cell"
                      title={f.mtime ? new Date(f.mtime).toLocaleString() : undefined}
                    >
                      {f.mtime ? relativeTime(f.mtime) : '—'}
                    </td>
                  </tr>
                ))}
                {dirs.length === 0 && files.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-sm text-muted-foreground">
                      {needle ? 'Nothing matches the filter.' : 'Empty.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
          {listing.data && listing.data.filesTotal > listing.data.files.length && (
            <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
              Showing the first {listing.data.files.length.toLocaleString()} of{' '}
              {listing.data.filesTotal.toLocaleString()} files in this folder.
            </p>
          )}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refresh cold storage listings?</DialogTitle>
            <DialogDescription>
              This lists the whole R2 backup bucket through the Cloudflare API, and Cloudflare bills
              every list call. Expect about {estCalls.toLocaleString()} calls (Class A, $
              {CLASS_A_USD_PER_MILLION.toFixed(2)} per million after the free 1M a month), roughly $
              {estUsd < 0.01 ? estUsd.toFixed(4) : estUsd.toFixed(2)}. It takes about 5 minutes. The
              current listing stays visible until the new one is done.
            </DialogDescription>
          </DialogHeader>
          {refresh.error && (
            <Alert tone="destructive">
              <AlertDescription>{refresh.error.message}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="default" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
              {refresh.isPending ? <Spinner size="xs" /> : <IconRefresh />}
              Refresh (costs API calls)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
